import { createWorkOrdersBatch } from "../../../../../../../../../../../../../api/kehrblatt/work-orders"
import { ApiError } from "../../../../../../../../../../../../../api/kehrblatt/client"
import type {
  LoadListOutput,
  CreateWorkOrdersOutput,
} from "../../../_shared/_logic/batch-upload-contract"
import {
  creationPayload,
  validateCreationFields,
} from "../../../_shared/_logic/work-order-creation"

/** The creation command needs only the owning overlay's submission state. */
export type WorkOrderCreationController = {
  organizationId: string
  state: {
    submissionPending: boolean
    frozenSubmission: {
      requestId: string
      output: LoadListOutput
      payload: WorkOrderCreatePayload[]
    } | null
  }
}

/** Keep the immutable request in the owning overlay until its result is known. */
export const persistPreparedWorkOrders = async (
  controller: WorkOrderCreationController,
  output: LoadListOutput,
): Promise<CreateWorkOrdersOutput> => {
  const state = controller.state

  if (state.submissionPending) throw new Error("Die Aufträge werden bereits angelegt.")

  if (!state.frozenSubmission) {
    const payload = output.selectedRows.map((row) => {
      if (!row.propertyUnitId || !row.orderFields)
        throw new Error("Der Auftragsentwurf ist unvollständig.")

      const error = validateCreationFields(row.orderFields)

      if (error) throw new Error(error)

      return creationPayload(row.propertyId, row.propertyUnitId, row.orderFields)
    })
    state.frozenSubmission = {
      requestId: crypto.randomUUID(),
      output: JSON.parse(JSON.stringify(output)),
      payload,
    }
  }

  const submitted = state.frozenSubmission
  state.submissionPending = true
  try {
    const orders = await createWorkOrdersBatch(submitted.payload, submitted.requestId)

    if (
      orders.length !== submitted.output.selectedRows.length ||
      new Set(orders.map((order) => order.id)).size !== orders.length ||
      orders.some(
        (order, index) =>
          order.organization_id !== controller.organizationId ||
          order.property_id !== submitted.payload[index].property_id ||
          order.property_unit_id !== submitted.payload[index].property_unit_id,
      )
    )
      throw new Error(
        "Die gespeicherten Aufträge konnten nicht eindeutig zugeordnet werden. Bitte denselben Vorgang erneut bestätigen.",
      )

    return {
      ...submitted.output,
      createdRows: orders.map((workOrder, index) => ({
        rowId: submitted.output.selectedRows[index].rowId,
        workOrder,
        context: submitted.output.selectedRows[index],
      })),
    }
  } catch (error) {
    // Validation rejects this payload before writes. Preserve the key for ambiguous failures,
    // including a replay denied after permissions or referenced objects have changed.
    if (error instanceof ApiError && [400, 422].includes(error.status))
      state.frozenSubmission = null

    throw error
  } finally {
    state.submissionPending = false
  }
}
